import React, { useEffect, useLayoutEffect, useRef, useCallback, useState, useMemo } from 'react';
import { toast } from '@/stores/toast.store';
import { useParams, Link, useNavigate } from '@tanstack/react-router';
import { useChatStore, type ChatMessage } from '@/stores/chat.store';
import { useAuthStore } from '@/stores/auth.store';
import { useOrgStore } from '@/stores/org.store';
import { useWebSocket, useSubscribeAreas } from '@/hooks/useWebSocket';
import { fetchMessages, sendMessage, editMessage, deleteMessage, markAreaRead, addReaction, removeReaction, acknowledgeMessage, markMessagesReadBatch, createReplyLater, updateReplyLater, listReplyLater, pinMessage, unpinMessage, fetchPins, bookmarkMessage, removeBookmark, toggleLinkPreview, checkDndStatus, fetchOrgMembers, updateProfile, presignVoiceMemo, completeVoiceMemo, fetchAreaNotificationSetting, updateAreaNotificationSetting } from '@/lib/api';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useUnreadStore } from '@/stores/unread.store';
import { MessageItem } from '@/components/chat/message-item';
import { MessageInput } from '@/components/chat/message-input';
import { TypingIndicator } from '@/components/chat/typing-indicator';
import { ConnectionStatus } from '@/components/chat/connection-status';
import { MediaViewer } from '@/components/chat/media-viewer';
import { ReceiptDetailModal } from '@/components/admin/receipt-detail-modal';
import { PinnedMessagesBar } from '@/components/chat/pinned-messages-bar';
import { useFileUpload } from '@/components/chat/file-upload';
import { ThreadSidePanel, ThreadViewPanel } from '@/components/chat/thread-side-panel';
import { TodosPanel } from '@/components/todos/todos-panel';
import { AreaThreadList } from '@/components/chat/area-thread-list';
import { AreaIcon } from '@/components/ui/icon-picker';
import { Tooltip } from '@/components/ui/tooltip';
import { AreaSettingsPanel } from '@/components/chat/area-settings-panel';
import { useThreadStore } from '@/stores/thread.store';
import { fetchThreads } from '@/lib/api';
import { nanoid } from 'nanoid';
import { EmptyState } from '@/components/ui/empty-state';
import { MessageListSkeleton } from '@/components/ui/skeleton';
import type { WSServerEvent, MessageAttachment } from '@goodchat/shared/types';

import { VOICE_MEMO_WAVEFORM_SAMPLES } from '@goodchat/shared/constants';
import { useOrgEmoji } from '@/hooks/useOrgEmoji';
import { SetStatusDialog } from '@/components/status/set-status-dialog';
import { DndSettings } from '@/components/status/dnd-settings';


// UUID v4 pattern — prevents using raw slug strings as IDs in API calls
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Max time gap (ms) between consecutive messages from the same author to be grouped. */
const MESSAGE_GROUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Returns true if a message should be rendered as a "continuation" (grouped)
 * message — i.e. it has the same author as the previous message, is within
 * the 5-minute grouping window, and is not an announcement or system message.
 */
function shouldGroupMessage(current: ChatMessage, previous: ChatMessage | undefined): boolean {
  if (!previous) return false;
  if (current.authorId !== previous.authorId) return false;
  // Don't group announcements or system messages
  if (current.messageType === 'announcement' || current.requiresAcknowledgment) return false;
  if (previous.messageType === 'announcement' || previous.requiresAcknowledgment) return false;
  // Don't group deleted messages
  if (current.isDeleted || previous.isDeleted) return false;
  // Check time window
  const currentTime = new Date(current.createdAt).getTime();
  const previousTime = new Date(previous.createdAt).getTime();
  return Math.abs(currentTime - previousTime) < MESSAGE_GROUP_WINDOW_MS;
}

/**
 * Read the duration of an audio file using the HTML5 Audio element.
 * Falls back to 0 if metadata cannot be loaded.
 */
function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const onLoaded = () => {
      const dur = isFinite(audio.duration) ? audio.duration : 0;
      cleanup();
      resolve(dur);
    };
    const onError = () => {
      cleanup();
      resolve(0);
    };
    const cleanup = () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('error', onError);
      URL.revokeObjectURL(url);
    };
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('error', onError);
    audio.src = url;
  });
}

/**
 * Generate a basic waveform (amplitude envelope) from an audio file using
 * the Web Audio API's decodeAudioData. Returns an array of normalized
 * amplitudes (0-1) with VOICE_MEMO_WAVEFORM_SAMPLES entries.
 * Falls back to an empty array on error.
 */
async function generateWaveformFromFile(file: File): Promise<number[]> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const audioCtx = new AudioContext();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    const rawData = audioBuffer.getChannelData(0);
    const samples = VOICE_MEMO_WAVEFORM_SAMPLES;
    const blockSize = Math.floor(rawData.length / samples);
    const waveform: number[] = [];
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      const start = i * blockSize;
      for (let j = start; j < start + blockSize; j++) {
        sum += Math.abs(rawData[j]);
      }
      waveform.push(sum / blockSize);
    }
    // Normalize to 0-1
    const max = Math.max(...waveform, 0.01);
    await audioCtx.close();
    return waveform.map((v) => v / max);
  } catch {
    return [];
  }
}

function ViewStyleToggle() {
  const viewStyle = useAuthStore((s) => s.user?.messageViewStyle ?? 'classic');
  const handleToggle = useCallback(() => {
    const next = viewStyle === 'classic' ? 'bubble' : 'classic';
    const user = useAuthStore.getState().user;
    if (user) {
      useAuthStore.getState().setAuth(
        { ...user, messageViewStyle: next },
        useAuthStore.getState().accessToken!,
        useAuthStore.getState().refreshToken!,
      );
    }
    updateProfile({ messageViewStyle: next }).catch(() => toast.error('Failed to save view style'));
  }, [viewStyle]);

  return (
    <Tooltip label={viewStyle === 'classic' ? 'Switch to bubbles' : 'Switch to classic'} position="bottom">
      <button
        onClick={handleToggle}
        className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        aria-label={viewStyle === 'classic' ? 'Switch to bubble view' : 'Switch to classic view'}
      >
        {viewStyle === 'classic' ? (
          /* Chat bubble icon — switch TO bubble */
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        ) : (
          /* List icon — switch TO classic */
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>
    </Tooltip>
  );
}

type NotificationLevel = 'all' | 'mentions' | 'muted';

/** Bell icon for "all" level */
function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'h-4 w-4'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  );
}

/** Bell with dot icon for "mentions" level */
function BellDotIcon({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'h-4 w-4'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      <circle cx="18" cy="5" r="3" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Bell-off icon for "muted" level */
function BellOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'h-4 w-4'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.143 17.082a24.248 24.248 0 003.718.33M17.412 14.147A8.955 8.955 0 0018 9.75V9A6 6 0 006.53 6.53M6 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m3.714 0a3 3 0 11-5.714 0" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
    </svg>
  );
}

function NotificationLevelIcon({ level, className }: { level: NotificationLevel; className?: string }) {
  switch (level) {
    case 'all': return <BellIcon className={className} />;
    case 'mentions': return <BellDotIcon className={className} />;
    case 'muted': return <BellOffIcon className={className} />;
  }
}

const NOTIFICATION_LEVEL_OPTIONS: { level: NotificationLevel; label: string; description: string }[] = [
  { level: 'all', label: 'All messages', description: 'Get notified for every message' },
  { level: 'mentions', label: 'Mentions only', description: 'Only when @mentioned' },
  { level: 'muted', label: 'Muted', description: 'No notifications' },
];

/**
 * Notification level dropdown for the area chat header.
 * Fetches current setting on mount and allows changing via Radix DropdownMenu.
 * Fires a callback when the level changes so the parent can update sidebar state.
 */
function NotificationLevelDropdown({
  orgId,
  areaId,
  onLevelChange,
}: {
  orgId: string;
  areaId: string;
  onLevelChange?: (level: NotificationLevel) => void;
}) {
  const [level, setLevel] = useState<NotificationLevel>('all');
  const [emailOverride, setEmailOverride] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch current level on mount / area change
  useEffect(() => {
    if (!orgId || !areaId) return;
    setLoading(true);
    fetchAreaNotificationSetting(orgId, areaId)
      .then((res) => {
        setLevel(res.level);
        setEmailOverride(res.emailOverride);
        onLevelChange?.(res.level);
      })
      .catch(() => {
        // Default to 'all' if fetch fails
        setLevel('all');
      })
      .finally(() => setLoading(false));
  }, [orgId, areaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback(
    (newLevel: NotificationLevel) => {
      if (newLevel === level) return;
      const prevLevel = level;
      setLevel(newLevel);
      onLevelChange?.(newLevel);
      updateAreaNotificationSetting(orgId, areaId, { level: newLevel }).catch(() => {
        // Revert on failure
        setLevel(prevLevel);
        onLevelChange?.(prevLevel);
        toast.error('Failed to update notification setting');
      });
    },
    [orgId, areaId, level, onLevelChange],
  );

  const handleEmailOverrideToggle = useCallback(() => {
    const newValue = !emailOverride;
    setEmailOverride(newValue);
    updateAreaNotificationSetting(orgId, areaId, { emailOverride: newValue }).catch(() => {
      setEmailOverride(!newValue);
      toast.error('Failed to update email setting');
    });
  }, [orgId, areaId, emailOverride]);

  const label = level === 'all' ? 'Notifications: All' : level === 'mentions' ? 'Notifications: Mentions' : 'Notifications: Muted';

  return (
    <DropdownMenu.Root>
      <Tooltip label={label} position="bottom">
        <DropdownMenu.Trigger asChild>
          <button
            className={`rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
              level === 'muted'
                ? 'text-gray-400 dark:text-gray-500'
                : level === 'mentions'
                  ? 'text-amber-500 dark:text-amber-400'
                  : 'text-gray-500 dark:text-gray-400'
            }`}
            aria-label={label}
            disabled={loading}
          >
            <NotificationLevelIcon level={level} className="h-4 w-4" />
          </button>
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[220px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
          sideOffset={4}
          align="end"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
            Notification level
          </DropdownMenu.Label>
          {NOTIFICATION_LEVEL_OPTIONS.map((option) => (
            <DropdownMenu.Item
              key={option.level}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
              onSelect={() => handleSelect(option.level)}
            >
              <NotificationLevelIcon
                level={option.level}
                className={`h-4 w-4 flex-shrink-0 ${
                  level === option.level ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                }`}
              />
              <div className="flex flex-col">
                <span className={`font-medium ${level === option.level ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  {option.label}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{option.description}</span>
              </div>
              {level === option.level && (
                <svg className="ml-auto h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            onSelect={(e) => { e.preventDefault(); handleEmailOverrideToggle(); }}
          >
            <svg className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            <div className="flex flex-col">
              <span className={`font-medium ${emailOverride ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                Email notifications
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Get email for every message in this room</span>
            </div>
            {emailOverride && (
              <svg className="ml-auto h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Unread banner with fade-out animation before unmount. */
function UnreadBanner({ count, onDismiss }: { count: number; onDismiss: () => void }) {
  const [visible, setVisible] = useState(count > 0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (count > 0) {
      setVisible(true);
      setFading(false);
    } else if (visible) {
      setFading(true);
      const timer = setTimeout(() => { setVisible(false); setFading(false); }, 300);
      return () => clearTimeout(timer);
    }
  }, [count]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  return (
    <button
      onClick={onDismiss}
      className={`absolute left-1/2 top-2 z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg transition-opacity duration-300 hover:bg-blue-700 ${fading ? 'opacity-0' : 'opacity-100'}`}
      aria-label="Dismiss unread indicator"
    >
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
      {count} unread message{count === 1 ? '' : 's'}
    </button>
  );
}

// Stable fallback to prevent Zustand selector from creating new refs on each store change
const EMPTY_MESSAGES: ChatMessage[] = [];

export function AreaChatPage() {
  const params = useParams({ strict: false }) as { orgSlug?: string; areaSlug?: string };
  // Use individual selectors to avoid subscribing to ALL org store changes
  const orgIdBySlug = useOrgStore((s) => s.orgIdBySlug);
  const areaIdBySlug = useOrgStore((s) => s.areaIdBySlug);
  const rawOrgSlug = params.orgSlug ?? '';
  const rawAreaSlug = params.areaSlug ?? '';
  // Resolve slug → UUID from maps; allow raw UUIDs through (for DM links); reject plain slugs
  const orgId = orgIdBySlug[rawOrgSlug] ?? (UUID_RE.test(rawOrgSlug) ? rawOrgSlug : '');
  const areaId = areaIdBySlug[rawAreaSlug] ?? (UUID_RE.test(rawAreaSlug) ? rawAreaSlug : '');

  const areas = useOrgStore((s) => s.areas);
  // Memoize area lookup — only recompute when areas array or areaId changes
  const area = useMemo(() => areas.find((a) => a.id === areaId), [areas, areaId]);

  // Admin/manager check
  const orgs = useAuthStore((s) => s.orgs);
  const currentOrg = useMemo(() => orgs.find((o) => o.id === orgId), [orgs, orgId]);
  const isAdmin = currentOrg?.role === 'owner' || currentOrg?.role === 'admin';
  const isManagerOrAbove = isAdmin || currentOrg?.role === 'manager';

  // Active tab state — local state instead of URL search params to avoid useSearch issues
  const [activeTab, setActiveTab] = useState<'chat' | 'threads'>('chat');

  // Threads tab split-pane selected thread
  const [threadsTabSelectedId, setThreadsTabSelectedId] = useState<string | null>(null);

  // Reset threads tab selection on area change
  useEffect(() => {
    setThreadsTabSelectedId(null);
    setTodosPanelOpen(false);
  }, [areaId]);

  // Thread side panel state
  const [threadPanelOpen, setThreadPanelOpen] = useState(false);
  const [todosPanelOpen, setTodosPanelOpen] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

  // DMs are NOT in the areas array (API filters them), so derive isDm from dms list
  const currentUserId = useAuthStore((s) => s.user?.id);
  const dms = useOrgStore((s) => s.dms);
  const isDm = useMemo(() => dms.some((d) => d.id === areaId), [dms, areaId]);

  // Unread counts for tab badges
  const chatUnread = useUnreadStore((s) => s.countsByArea[areaId] ?? 0);
  const EMPTY_THREADS: import('@/stores/thread.store').ThreadListItem[] = useMemo(() => [], []);
  const areaThreads = useThreadStore((s) => s.threadsByArea[areaId] ?? EMPTY_THREADS);
  const threadUnread = useMemo(() => areaThreads.reduce((sum, t) => sum + (t.unreadCount ?? 0), 0), [areaThreads]);

  // Fetch threads on area mount for unread badge (not just when switching to Threads tab)
  useEffect(() => {
    if (!orgId || !areaId || isDm) return;
    fetchThreads(orgId, areaId, 'all')
      .then((data) => useThreadStore.getState().setThreads(areaId, data.threads))
      .catch(() => toast.error('Failed to load threads'));
  }, [orgId, areaId, isDm]);

  // Area settings panel state
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);

  // Mobile overflow menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileMenuOpen]);

  const areaName = useMemo(() => {
    if (isDm) {
      const dm = dms.find((d) => d.id === areaId);
      if (dm) {
        const others = dm.participants.filter((p) => p.userId !== currentUserId);
        if (others.length > 0) return others.map((p) => p.displayName).join(', ');
      }
    }
    return area?.name ?? 'Chat';
  }, [isDm, area, areaId, dms, currentUserId]);

  return (
    /*
      `h-full` is 100% of <main>, but <main> also holds a 48px mobile header
      above this. On a phone that pushed the whole chat column 48px past the
      bottom of the screen and took the composer with it — measured at 375x812,
      the textarea's bottom edge sat at 848. `flex-1 min-h-0` fills what is
      actually left instead. min-h-0 is not optional: without it a flex child
      refuses to shrink below its content and the message list stops scrolling.
    */
    <div className="flex min-h-0 flex-1">
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Header */}
      <header className="flex h-12 flex-shrink-0 items-center justify-between border-b border-gray-200 px-3 sm:h-14 sm:px-4 dark:border-gray-800">
        <div className="flex min-w-0 items-center gap-2">
          {!isDm && <AreaIcon icon={area?.icon ?? null} iconColor={area?.iconColor ?? null} className="h-5 w-5 flex-shrink-0" />}
          <span className="truncate text-base font-semibold text-gray-900 sm:text-lg dark:text-gray-100">
            {areaName}
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
          {!isDm && (
            <>
              {/* Desktop: show all buttons inline */}
              {isManagerOrAbove && (
                <Link
                  to="/org/$orgSlug/$areaSlug/announcement/new"
                  params={{ orgSlug: rawOrgSlug, areaSlug: rawAreaSlug }}
                  className="hidden items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-amber-600 transition-colors hover:bg-amber-50 sm:flex dark:text-amber-400 dark:hover:bg-amber-900/30"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                  Announce
                </Link>
              )}
              <Link
                to="/org/$orgSlug/$areaSlug/gallery"
                params={{ orgSlug: params.orgSlug ?? '', areaSlug: params.areaSlug ?? '' }}
                className="hidden items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 sm:flex dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                </svg>
                Gallery
              </Link>
              <button
                onClick={() => {
                  setTodosPanelOpen((v) => !v);
                  // One drawer at a time — two open panes leave the chat
                  // column too narrow to read.
                  if (!todosPanelOpen) setThreadPanelOpen(false);
                }}
                className={`hidden items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors sm:flex ${
                  todosPanelOpen
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Todos
              </button>
              <span className="hidden sm:inline"><ViewStyleToggle /></span>
              <span className="hidden sm:inline">
                <NotificationLevelDropdown orgId={orgId} areaId={areaId} onLevelChange={(lvl) => useOrgStore.getState().setAreaNotificationLevel(areaId, lvl)} />
              </span>
              {isManagerOrAbove && (
                <button
                  onClick={() => setSettingsPanelOpen((v) => !v)}
                  className={`hidden rounded-lg p-1.5 transition-colors sm:block ${
                    settingsPanelOpen
                      ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                  title="Room settings"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}

              {/* Mobile: notification dropdown (always visible) */}
              <span className="sm:hidden">
                <NotificationLevelDropdown orgId={orgId} areaId={areaId} onLevelChange={(lvl) => useOrgStore.getState().setAreaNotificationLevel(areaId, lvl)} />
              </span>
              {/* Mobile: overflow menu */}
              <div className="relative sm:hidden" ref={mobileMenuRef}>
                <button
                  onClick={() => setMobileMenuOpen((v) => !v)}
                  className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  aria-label="More options"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                  </svg>
                </button>
                {mobileMenuOpen && (
                  <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                    {isManagerOrAbove && (
                      <Link
                        to="/org/$orgSlug/$areaSlug/announcement/new"
                        params={{ orgSlug: rawOrgSlug, areaSlug: rawAreaSlug }}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-amber-600 transition-colors hover:bg-gray-50 dark:text-amber-400 dark:hover:bg-gray-800"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                        Announce
                      </Link>
                    )}
                    <Link
                      to="/org/$orgSlug/$areaSlug/gallery"
                      params={{ orgSlug: params.orgSlug ?? '', areaSlug: params.areaSlug ?? '' }}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                      </svg>
                      Gallery
                    </Link>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        const user = useAuthStore.getState().user;
                        if (user) {
                          const next = (user.messageViewStyle ?? 'classic') === 'classic' ? 'bubble' : 'classic';
                          useAuthStore.getState().setAuth(
                            { ...user, messageViewStyle: next },
                            useAuthStore.getState().accessToken!,
                            useAuthStore.getState().refreshToken!,
                          );
                          updateProfile({ messageViewStyle: next }).catch(() => toast.error('Failed to save view style'));
                        }
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Toggle view style
                    </button>
                    {isManagerOrAbove && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setSettingsPanelOpen((v) => !v);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Settings
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* DM areas: keep Threads button in header */}
          {isDm && (
            <button
              onClick={() => {
                setThreadPanelOpen((v) => !v);
                if (!threadPanelOpen) setSelectedThreadId(null);
              }}
              className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors sm:px-3 ${
                threadPanelOpen
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              <span className="hidden sm:inline">Threads</span>
            </button>
          )}
        </div>
      </header>

      {/* Chat / Threads segmented control — only for non-DM rooms.
          Design (DESIGN.md move #3): unmissable segmented tabs + a plain-
          language hint so the chat/threads distinction lands for everyone. */}
      {!isDm && (
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-gray-100 px-4 py-2 dark:border-gray-800">
          <div className="inline-flex gap-0.5 rounded-[10px] border border-gray-200 bg-gray-100 p-[3px] dark:border-gray-700 dark:bg-gray-900" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'chat'}
              onClick={() => setActiveTab('chat')}
              className={`flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
                activeTab === 'chat'
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Chat
              {activeTab !== 'chat' && chatUnread > 0 && (
                <span className="min-w-[18px] rounded-full bg-blue-100 px-1.5 py-0.5 text-center text-[10px] font-bold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  {chatUnread > 99 ? '99+' : chatUnread}
                </span>
              )}
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'threads'}
              onClick={() => setActiveTab('threads')}
              className={`flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
                activeTab === 'threads'
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              Threads
              {activeTab !== 'threads' && threadUnread > 0 && (
                <span className="min-w-[18px] rounded-full bg-blue-100 px-1.5 py-0.5 text-center text-[10px] font-bold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  {threadUnread > 99 ? '99+' : threadUnread}
                </span>
              )}
            </button>
          </div>
          <span className="hidden truncate text-xs text-gray-400 sm:block dark:text-gray-500">
            Chat is the live conversation · Threads are named topics you can resolve
          </span>
        </div>
      )}

      {/* Tab content — each in its own component for stable hook order */}
      {!isDm && activeTab === 'threads' ? (
        <div className="flex min-h-0 flex-1">
          {/* Thread list — fixed width on large screens, full width on small */}
          <div className={`flex flex-shrink-0 flex-col border-r border-gray-200 dark:border-gray-800 ${threadsTabSelectedId ? 'hidden lg:flex lg:w-[400px]' : 'w-full'}`}>
            <AreaThreadList
              orgId={orgId}
              areaId={areaId}
              areaName={areaName}
              orgSlug={rawOrgSlug}
              areaSlug={rawAreaSlug}
              selectedThreadId={threadsTabSelectedId}
              onThreadClick={(threadId) => setThreadsTabSelectedId(threadId)}
            />
          </div>
          {/* Thread detail — right side */}
          {threadsTabSelectedId ? (
            <div className="flex min-w-0 flex-1 flex-col">
              <ThreadViewPanel
                orgId={orgId}
                areaId={areaId}
                threadId={threadsTabSelectedId}
                onBack={() => setThreadsTabSelectedId(null)}
                onClose={() => setThreadsTabSelectedId(null)}
              />
            </div>
          ) : (
            <div className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Select a thread to view</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <ChatTabContent
          orgId={orgId}
          areaId={areaId}
          isDm={isDm}
          isAdmin={isAdmin}
          isManagerOrAbove={isManagerOrAbove}
          onViewThread={(threadId) => {
            setSelectedThreadId(threadId);
            setThreadPanelOpen(true);
          }}
        />
      )}
    </div>

    {/* Thread side panel */}
    {threadPanelOpen && (
      <ThreadSidePanel
        orgId={orgId}
        areaId={areaId}
        areaName={areaName}
        threadId={selectedThreadId}
        onSelectThread={setSelectedThreadId}
        onClose={() => {
          setThreadPanelOpen(false);
          setSelectedThreadId(null);
        }}
      />
    )}

    {todosPanelOpen && (
      <TodosPanel
        orgId={orgId}
        areaId={areaId}
        areaName={areaName}
        onClose={() => setTodosPanelOpen(false)}
        onOpenItem={(item) => {
          // Only thread-anchored rows can be navigated — the web app has no
          // jump-to-message, so other rows stay non-clickable rather than
          // pretending to go somewhere.
          if (!item.threadId) return;
          setTodosPanelOpen(false);
          setSelectedThreadId(item.threadId);
          setThreadPanelOpen(true);
        }}
      />
    )}

    {/* Area settings panel */}
    {settingsPanelOpen && !isDm && (
      <AreaSettingsPanel
        orgId={orgId}
        areaId={areaId}
        onClose={() => setSettingsPanelOpen(false)}
      />
    )}
    </div>
  );
}

/**
 * Extracted chat content component — all chat hooks live here so switching
 * between Chat/Threads tabs doesn't change hook count in the parent.
 *
 * Wrapped in React.memo to prevent parent re-renders from cascading in.
 * All props are either primitives, stable references (useState setter), or
 * memoized objects — ensuring memo comparison works correctly.
 */
const ChatTabContent = React.memo(function ChatTabContent({
  orgId,
  areaId,
  isDm,
  isAdmin,
  isManagerOrAbove,
  onViewThread,
}: {
  orgId: string;
  areaId: string;
  isDm: boolean;
  isAdmin: boolean;
  isManagerOrAbove: boolean;
  onViewThread?: (threadId: string) => void;
}) {
  // Read params directly instead of receiving as prop (useParams may return
  // a new object reference on every render, which defeats React.memo)
  const params = useParams({ strict: false }) as { orgSlug?: string; areaSlug?: string };
  const currentUser = useAuthStore((s) => s.user);
  const viewStyle = useAuthStore((s) => s.user?.messageViewStyle ?? 'classic');
  const orgs = useAuthStore((s) => s.orgs);
  const currentOrg = orgs.find((o) => o.id === orgId);

  // Use stable EMPTY_MESSAGES fallback to prevent new [] ref on every store change
  const messages = useChatStore((s) => s.messagesByArea[areaId] ?? EMPTY_MESSAGES);
  const hasMore = useChatStore((s) => s.hasMoreByArea[areaId] ?? true);
  const isConnected = useChatStore((s) => s.isConnected);
  // Access actions via getState() to avoid subscribing to ALL chat store changes.
  // Zustand actions are stable references — they never change between renders.
  const {
    setMessages,
    mergeMessages,
    prependMessages,
    addMessage,
    addPendingMessage,
    replacePendingMessage,
    markPendingFailed,
    removePendingMessage,
    updateMessageContent,
    markMessageDeleted,
    setTypers,
    setReplyingTo,
    addReactionToMessage,
    removeReactionFromMessage,
  } = useChatStore.getState();

  // Custom emoji for reactions
  const { emoji: customEmojiList, emojiByShortcode: customEmojiMap } = useOrgEmoji(orgId || undefined);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Instant scroll on initial load — useLayoutEffect fires before paint
  const isInitialScrollRef = useRef(true);

  // Unread banner state — captured before markAreaRead zeroes the count
  const [unreadBannerCount, setUnreadBannerCount] = useState(0);
  const unreadBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll-to-bottom floating button state
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const isNearBottomRef = useRef(true);

  // Media viewer state
  const [viewingMedia, setViewingMedia] = useState<MessageAttachment | null>(null);

  // Receipt detail modal state
  const [receiptMessageId, setReceiptMessageId] = useState<string | null>(null);

  // Voice Stream state
  const [voiceStreamEnabled, setVoiceStreamEnabled] = useState(false);
  const [autoPlayMessageId, setAutoPlayMessageId] = useState<string | null>(null);

  // Slash command dialogs
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showDndDialog, setShowDndDialog] = useState(false);
  const slashNavigate = useNavigate();

  const handleSlashCommand = useCallback((command: string) => {
    if (command === 'status') setShowStatusDialog(true);
    else if (command === 'dnd') setShowDndDialog(true);
    else if (command === 'thread') {
      slashNavigate({ to: '/org/$orgSlug/$areaSlug/threads/new', params: { orgSlug: params.orgSlug ?? '', areaSlug: params.areaSlug ?? '' } });
    }
  }, [slashNavigate, params.orgSlug, params.areaSlug]);

  // DND confirmation state
  const [dndConfirmOpen, setDndConfirmOpen] = useState(false);
  const [dndUsers, setDndUsers] = useState<Array<{ userId: string; displayName: string; isDnd: boolean; isOutsideWorkingHours: boolean }>>([]);
  const [pendingSendArgs, setPendingSendArgs] = useState<{ content: string; replyToMessageId?: string; attachmentIds?: string[]; voiceMemoId?: string } | null>(null);

  // Cache org members for @mention resolution
  const orgMembersRef = useRef<Array<{ userId: string; displayName: string }>>([]);
  useEffect(() => {
    if (!orgId) return;
    fetchOrgMembers(orgId).then((res) => {
      orgMembersRef.current = res.members.map((m) => ({ userId: m.userId, displayName: m.displayName }));
    }).catch(() => toast.error('Failed to load org members'));
  }, [orgId]);

  // File upload (lifted for drag-and-drop support)
  const fileUpload = useFileUpload();
  const { processFiles } = fileUpload;

  // Drag-and-drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Subscribe to area via WS
  useSubscribeAreas(areaId ? [areaId] : []);

  // Catch up on read state when the user returns to the tab (messages that
  // arrived while unfocused incremented the badge instead of auto-reading)
  useEffect(() => {
    if (!orgId || !areaId) return;
    const onFocus = () => {
      if ((useUnreadStore.getState().countsByArea[areaId] ?? 0) > 0) {
        markAreaRead(orgId, areaId).catch(() => {});
        useUnreadStore.getState().markAreaRead(areaId);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [orgId, areaId]);

  // Handle WS events
  const wsHandler = useCallback(
    (event: WSServerEvent) => {
      if (!areaId) return;

      switch (event.event) {
        case 'message.new':
          if (event.areaId === areaId) {
            addMessage(areaId, event.message as unknown as ChatMessage);
            // Only mark read when the tab is actually focused — a background
            // tab silently eating unreads made badges untrustworthy. The
            // focus listener below catches up when the user returns.
            if (orgId && document.hasFocus()) {
              markAreaRead(orgId, areaId).catch(() => toast.error('Failed to mark area as read'));
              useUnreadStore.getState().markAreaRead(areaId);
            } else {
              useUnreadStore.getState().incrementArea(areaId);
            }
          }
          break;
        case 'message.edited':
          if (event.areaId === areaId) {
            updateMessageContent(event.messageId, areaId, event.content, event.editedAt);
          }
          break;
        case 'message.deleted':
          if (event.areaId === areaId) {
            markMessageDeleted(event.messageId, areaId);
          }
          break;
        case 'typing.update':
          if (event.areaId === areaId && !event.threadId) {
            setTypers(areaId, event.typers);
          }
          break;
        case 'reaction.added':
          if (event.areaId === areaId) {
            addReactionToMessage(event.messageId, areaId, event.emoji, event.user, currentUser?.id ?? '');
          }
          break;
        case 'reaction.removed':
          if (event.areaId === areaId) {
            removeReactionFromMessage(event.messageId, areaId, event.emoji, event.userId, currentUser?.id ?? '');
          }
          break;
        case 'reconnected':
          // The socket re-subscribed on open, but anything sent during the gap
          // was missed — refetch and merge (preserving pending optimistic sends).
          if (orgId) {
            fetchMessages(orgId, areaId)
              .then((result) => mergeMessages(areaId, result.data.reverse(), result.hasMore))
              .catch(() => {});
          }
          break;
      }
    },
    [areaId, orgId, addMessage, mergeMessages, updateMessageContent, markMessageDeleted, setTypers, addReactionToMessage, removeReactionFromMessage, currentUser?.id],
  );

  useWebSocket(wsHandler);

  // Load initial messages and mark area as read
  useEffect(() => {
    if (!orgId || !areaId) return;
    initialLoadDoneRef.current = false;
    setInitialLoadDone(false);
    isInitialScrollRef.current = true;

    // Capture unread count BEFORE marking as read so the banner can show it
    const capturedUnread = useUnreadStore.getState().countsByArea[areaId] ?? 0;
    setUnreadBannerCount(capturedUnread);
    // Auto-dismiss banner after 5 seconds
    if (unreadBannerTimerRef.current) clearTimeout(unreadBannerTimerRef.current);
    if (capturedUnread > 0) {
      unreadBannerTimerRef.current = setTimeout(() => setUnreadBannerCount(0), 5000);
    }

    fetchMessages(orgId, areaId)
      .then((result) => {
        // Messages come in desc order (newest first), reverse for display
        setMessages(areaId, result.data.reverse(), result.hasMore);
        initialLoadDoneRef.current = true;
        setInitialLoadDone(true);
      })
      .catch(() => {
        toast.error('Failed to load messages');
        setInitialLoadDone(true);
      });

    // Mark area as read
    markAreaRead(orgId, areaId).catch(() => toast.error('Failed to mark area as read'));
    useUnreadStore.getState().markAreaRead(areaId);
  }, [orgId, areaId, setMessages]);

  // Load older messages on scroll to top + track distance from bottom
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Track distance from bottom for scroll-to-bottom button
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const nearBottom = distanceFromBottom < 200;
    isNearBottomRef.current = nearBottom;
    setShowScrollToBottom(!nearBottom);
    if (nearBottom) {
      setNewMessageCount(0);
    }

    if (isLoadingRef.current || !hasMore || !orgId || !areaId) return;

    if (container.scrollTop < 100) {
      const oldestMessage = messages[0];
      if (!oldestMessage) return;

      isLoadingRef.current = true;
      const prevHeight = container.scrollHeight;

      fetchMessages(orgId, areaId, { before: oldestMessage.id })
        .then((result) => {
          prependMessages(areaId, result.data.reverse(), result.hasMore);
          // Maintain scroll position
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - prevHeight;
          });
        })
        .catch(() => toast.error('Failed to load older messages'))
        .finally(() => {
          isLoadingRef.current = false;
        });
    }
  }, [orgId, areaId, messages, hasMore, prependMessages]);

  // Instant scroll to bottom on initial message load — runs before browser paint
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !messages.length) return;
    if (isInitialScrollRef.current && initialLoadDoneRef.current) {
      container.scrollTop = container.scrollHeight;
      isInitialScrollRef.current = false;
      // Sync count so the existing smooth-scroll useEffect doesn't double-fire
      prevMessageCountRef.current = messages.length;
    }
  }, [messages.length]);

  // Dismiss unread banner on user scroll (after initial load)
  const handleScrollForBanner = useCallback(() => {
    if (!isInitialScrollRef.current && unreadBannerCount > 0) {
      setUnreadBannerCount(0);
    }
  }, [unreadBannerCount]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScrollForBanner);
    return () => container.removeEventListener('scroll', handleScrollForBanner);
  }, [handleScrollForBanner]);

  // Actually dispatch the message (called directly or after DND confirmation)
  const doSendMessage = useCallback(
    (content: string, replyToMessageId?: string, attachmentIds?: string[], voiceMemoId?: string, notifyAnywayUserIds?: string[]) => {
      if (!orgId || !areaId || !currentUser) return;

      const messageType = voiceMemoId ? 'voice' : attachmentIds?.length ? 'media' : 'text';
      const tempId = nanoid();
      const pendingMessage: ChatMessage = {
        id: tempId,
        areaId,
        threadId: null,
        authorId: currentUser.id,
        content: content || null,
        messageType,
        replyToMessageId: replyToMessageId ?? null,
        sequenceNumber: Date.now(),
        isDeleted: false,
        editedAt: null,
        createdAt: new Date().toISOString(),
        author: {
          id: currentUser.id,
          fullName: currentUser.fullName,
          displayName: currentUser.displayName,
          profilePictureUrl: currentUser.profilePictureUrl,
        },
        replyTo: null,
        tempId,
        isPending: true,
      };

      addPendingMessage(areaId, pendingMessage);

      if (isConnected) {
        sendMessage(orgId, areaId, {
          content: content || '',
          replyToMessageId,
          attachmentIds,
          voiceMemoId,
          messageType: voiceMemoId ? 'voice' : undefined,
          notifyAnywayUserIds,
        })
          .then((result) => {
            replacePendingMessage(tempId, result.message);
          })
          .catch(() => {
            markPendingFailed(tempId);
          });
      } else {
        useChatStore.getState().addToOfflineQueue({
          tempId,
          orgId,
          areaId,
          content: content || '',
          replyToMessageId,
        });
      }
    },
    [orgId, areaId, currentUser, isConnected, addPendingMessage, replacePendingMessage, markPendingFailed],
  );

  // Upload a dropped audio file as a voice memo (presign -> S3 -> complete -> send)
  const handleDroppedAudioAsVoiceMemo = useCallback(
    async (file: File) => {
      try {
        // Determine file extension from MIME type
        const extMap: Record<string, string> = {
          'audio/webm': 'webm',
          'audio/mp4': 'm4a',
          'audio/mpeg': 'mp3',
          'audio/ogg': 'ogg',
          'audio/wav': 'wav',
        };
        const ext = extMap[file.type] ?? file.name.split('.').pop() ?? 'audio';
        const fileName = `voice-memo.${ext}`;

        // Get audio duration and waveform in parallel
        const [durationSeconds, waveformData] = await Promise.all([
          getAudioDuration(file),
          generateWaveformFromFile(file),
        ]);

        // 1. Presign
        const { uploadUrl, fileKey, voiceMemoId } = await presignVoiceMemo({
          fileName,
          fileType: file.type,
          fileSize: file.size,
          orgId,
        });

        // 2. Upload to S3
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        // 3. Complete
        await completeVoiceMemo(voiceMemoId, {
          fileKey,
          durationSeconds,
          waveformData,
          orgId,
        });

        // 4. Send message with voiceMemoId
        doSendMessage('', undefined, undefined, voiceMemoId);
      } catch {
        toast.error('Failed to send audio as voice memo');
      }
    },
    [orgId, doSendMessage],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;

      // Separate audio files from non-audio files
      const audioFiles: File[] = [];
      const otherFiles: File[] = [];
      for (const file of Array.from(files)) {
        if (file.type.startsWith('audio/')) {
          audioFiles.push(file);
        } else {
          otherFiles.push(file);
        }
      }

      // Handle non-audio files as regular attachments
      if (otherFiles.length > 0) {
        processFiles(otherFiles);
      }

      // Handle audio files as voice memos (one per file, since each becomes its own message)
      for (const audioFile of audioFiles) {
        handleDroppedAudioAsVoiceMemo(audioFile);
      }
    },
    [processFiles, handleDroppedAudioAsVoiceMemo],
  );

  // Send message handler — checks DND status of @mentioned users before sending
  const handleSend = useCallback(
    (content: string, replyToMessageId?: string, attachmentIds?: string[], voiceMemoId?: string) => {
      if (!orgId || !areaId || !currentUser) return;

      // Extract @mentions from content to check DND status
      const mentionRegex = /@(\S+)/g;
      const mentionedUserIds: string[] = [];
      let match;
      while ((match = mentionRegex.exec(content || '')) !== null) {
        const slug = match[1].toLowerCase();
        // Skip @everyone and @here — those are group mentions, not individual DND checks
        if (slug === 'everyone' || slug === 'here') continue;
        // Resolve slug to userId via cached org members
        const normalizedSlug = slug.replace(/\./g, ' ').toLowerCase();
        const member = orgMembersRef.current.find(
          (m) => m.displayName.toLowerCase().replace(/\s+/g, ' ') === normalizedSlug ||
                 m.displayName.toLowerCase().replace(/\s+/g, '.') === slug,
        );
        if (member && member.userId !== currentUser.id) {
          mentionedUserIds.push(member.userId);
        }
      }

      // If no individual user mentions, just send directly
      if (mentionedUserIds.length === 0) {
        doSendMessage(content, replyToMessageId, attachmentIds, voiceMemoId);
        return;
      }

      // Check DND status for mentioned users
      const uniqueUserIds = [...new Set(mentionedUserIds)];
      checkDndStatus(uniqueUserIds)
        .then((res) => {
          const usersInDnd = res.users.filter((u) => u.isDnd || u.isOutsideWorkingHours);
          if (usersInDnd.length === 0) {
            // No one in DND, send normally
            doSendMessage(content, replyToMessageId, attachmentIds, voiceMemoId);
          } else {
            // Show DND confirmation dialog
            setDndUsers(usersInDnd);
            setPendingSendArgs({ content, replyToMessageId, attachmentIds, voiceMemoId });
            setDndConfirmOpen(true);
          }
        })
        .catch(() => {
          // If DND check fails, send normally
          doSendMessage(content, replyToMessageId, attachmentIds, voiceMemoId);
        });
    },
    [orgId, areaId, currentUser, doSendMessage],
  );

  // DND confirmation handlers
  const handleDndSendQuietly = useCallback(() => {
    if (pendingSendArgs) {
      doSendMessage(pendingSendArgs.content, pendingSendArgs.replyToMessageId, pendingSendArgs.attachmentIds, pendingSendArgs.voiceMemoId);
    }
    setDndConfirmOpen(false);
    setDndUsers([]);
    setPendingSendArgs(null);
  }, [pendingSendArgs, doSendMessage]);

  const handleDndNotifyAnyway = useCallback(() => {
    if (pendingSendArgs) {
      const notifyAnywayUserIds = dndUsers.map((u) => u.userId);
      doSendMessage(pendingSendArgs.content, pendingSendArgs.replyToMessageId, pendingSendArgs.attachmentIds, pendingSendArgs.voiceMemoId, notifyAnywayUserIds);
    }
    setDndConfirmOpen(false);
    setDndUsers([]);
    setPendingSendArgs(null);
  }, [pendingSendArgs, dndUsers, doSendMessage]);

  const handleDndCancel = useCallback(() => {
    setDndConfirmOpen(false);
    setDndUsers([]);
    setPendingSendArgs(null);
  }, []);

  // Escape key dismisses DND confirmation dialog
  useEffect(() => {
    if (!dndConfirmOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDndCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dndConfirmOpen, handleDndCancel]);

  // Edit message handler
  const handleEdit = useCallback(
    (messageId: string, content: string) => {
      editMessage(messageId, content).catch(() => toast.error('Failed to edit message'));
    },
    [],
  );

  // Delete message handler
  const handleDelete = useCallback(
    (messageId: string) => {
      deleteMessage(messageId).catch(() => toast.error('Failed to delete message'));
    },
    [],
  );

  // Reply handler
  const handleReply = useCallback(
    (message: ChatMessage) => {
      setReplyingTo(areaId, message);
    },
    [areaId, setReplyingTo],
  );

  // Retry failed message
  const handleRetry = useCallback(
    (message: ChatMessage) => {
      if (!orgId || !areaId) return;
      removePendingMessage(message.tempId!);
      handleSend(message.content!, message.replyToMessageId ?? undefined);
    },
    [orgId, areaId, removePendingMessage, handleSend],
  );

  // Reaction handler — toggles reaction on/off
  const handleReact = useCallback(
    (messageId: string, emoji: string) => {
      if (!areaId || !currentUser) return;
      const msg = messages.find((m) => m.id === messageId);
      const existing = msg?.reactions?.find((r) => r.emoji === emoji);
      if (existing?.includesMe) {
        removeReactionFromMessage(messageId, areaId, emoji, currentUser.id, currentUser.id);
        removeReaction(messageId, emoji).catch(() => toast.error('Failed to remove reaction'));
      } else {
        addReactionToMessage(messageId, areaId, emoji, { userId: currentUser.id, displayName: currentUser.displayName }, currentUser.id);
        addReaction(messageId, emoji).catch(() => toast.error('Failed to add reaction'));
      }
    },
    [areaId, currentUser, messages, addReactionToMessage, removeReactionFromMessage],
  );

  // Acknowledge handler
  const handleAcknowledge = useCallback(
    (messageId: string) => {
      if (!areaId || !currentUser) return;
      // Optimistic update
      const msg = messages.find((m) => m.id === messageId);
      if (msg?.acknowledgmentSummary) {
        useChatStore.getState().updateAcknowledgment(
          messageId,
          areaId,
          msg.acknowledgmentSummary.total,
          msg.acknowledgmentSummary.acknowledged + 1,
        );
      }
      acknowledgeMessage(messageId).catch(() => toast.error('Failed to acknowledge message'));
    },
    [areaId, currentUser, messages],
  );

  // View receipts handler
  const handleViewReceipts = useCallback(
    (messageId: string) => {
      setReceiptMessageId(messageId);
    },
    [],
  );

  // Auto-mark messages as read (batch, debounced, chunked to API max of 100)
  const readTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!messages.length) return;
    if (readTimerRef.current) clearTimeout(readTimerRef.current);
    readTimerRef.current = setTimeout(() => {
      const unreadIds = messages
        .filter((m) => !m.isPending && !m.isFailed && m.authorId !== currentUser?.id)
        .map((m) => m.id);
      if (unreadIds.length > 0) {
        // Chunk into batches of 100 (API Zod validation max)
        const BATCH_SIZE = 100;
        for (let i = 0; i < unreadIds.length; i += BATCH_SIZE) {
          const chunk = unreadIds.slice(i, i + BATCH_SIZE);
          markMessagesReadBatch(chunk).catch(() => toast.error('Failed to mark messages as read'));
        }
      }
    }, 2000);
    return () => {
      if (readTimerRef.current) clearTimeout(readTimerRef.current);
    };
  }, [messages.length, currentUser?.id]);

  // Voice Stream: auto-play next voice memo
  const handleVoicePlaybackEnd = useCallback(
    (messageId: string) => {
      if (!voiceStreamEnabled) return;
      // Find the next voice message after the one that just ended
      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx < 0) return;
      for (let i = idx + 1; i < messages.length; i++) {
        if (messages[i].voiceMemo && !messages[i].isDeleted) {
          setAutoPlayMessageId(messages[i].id);
          return;
        }
      }
      setAutoPlayMessageId(null);
    },
    [voiceStreamEnabled, messages],
  );

  // Reply later handler + tracking
  const [replyLaterMessageIds, setReplyLaterMessageIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    listReplyLater({ status: 'active', limit: 50 })
      .then((res) => {
        const ids = new Set<string>();
        for (const item of res.items) {
          if (item.messageId) ids.add(item.messageId as string);
        }
        setReplyLaterMessageIds(ids);
      })
      .catch(() => toast.error('Failed to load reply later items'));
  }, [areaId]);

  const handleFlagReplyLater = useCallback(
    (messageId: string) => {
      createReplyLater(messageId)
        .then(() => {
          setReplyLaterMessageIds((prev) => new Set(prev).add(messageId));
        })
        .catch(() => toast.error('Failed to flag for reply later'));
    },
    [],
  );

  const handleReplyLaterWithSnooze = useCallback(
    (messageId: string, snoozedUntil: string) => {
      createReplyLater(messageId)
        .then((res) => {
          setReplyLaterMessageIds((prev) => new Set(prev).add(messageId));
          if (res.replyLater?.id) {
            updateReplyLater(res.replyLater.id as string, { snoozedUntil }).catch(() => toast.error('Failed to snooze'));
          }
        })
        .catch(() => toast.error('Failed to flag for reply later'));
    },
    [],
  );

  // Pin handler — toggle pin/unpin
  // Use ref so the callback always sees the latest map (avoids stale closure with React.memo)
  const pinIdMapRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!orgId || !areaId) return;
    fetchPins(orgId, areaId).then((res) => {
      const map: Record<string, string> = {};
      for (const p of res.pins) map[p.message.id] = p.id;
      pinIdMapRef.current = map;
    }).catch(() => toast.error('Failed to load pins'));
  }, [orgId, areaId]);

  const handlePin = useCallback(
    (messageId: string) => {
      if (!orgId || !areaId) return;
      const existingPinId = pinIdMapRef.current[messageId];
      if (existingPinId) {
        // Optimistic unpin
        useChatStore.getState().togglePinned(messageId, areaId, false);
        delete pinIdMapRef.current[messageId];
        unpinMessage(orgId, areaId, existingPinId).catch(() => {
          // Revert on failure
          useChatStore.getState().togglePinned(messageId, areaId, true);
          pinIdMapRef.current[messageId] = existingPinId;
          toast.error('Failed to unpin message');
        });
      } else {
        // Optimistic pin
        useChatStore.getState().togglePinned(messageId, areaId, true);
        pinMessage(orgId, areaId, messageId).then((res) => {
          pinIdMapRef.current[messageId] = res.pin.id;
        }).catch(() => {
          // Revert on failure
          useChatStore.getState().togglePinned(messageId, areaId, false);
          toast.error('Failed to pin message');
        });
      }
    },
    [orgId, areaId],
  );

  // Bookmark handler — toggle bookmark
  // Use ref so the callback always sees the latest map (avoids stale closure with React.memo)
  const bookmarkIdMapRef = useRef<Record<string, string>>({});
  const handleBookmark = useCallback(
    (messageId: string) => {
      // Read current pinned/bookmarked state from the store directly
      const msgs = useChatStore.getState().messagesByArea[areaId] ?? [];
      const msg = msgs.find((m) => m.id === messageId);
      if (msg?.isBookmarked) {
        const bmId = bookmarkIdMapRef.current[messageId];
        if (bmId) {
          // Optimistic unbookmark
          useChatStore.getState().toggleBookmarked(messageId, areaId, false);
          delete bookmarkIdMapRef.current[messageId];
          removeBookmark(bmId).catch(() => {
            // Revert on failure
            useChatStore.getState().toggleBookmarked(messageId, areaId, true);
            bookmarkIdMapRef.current[messageId] = bmId;
            toast.error('Failed to remove bookmark');
          });
        }
      } else {
        // Optimistic bookmark
        useChatStore.getState().toggleBookmarked(messageId, areaId, true);
        bookmarkMessage(messageId).then((res) => {
          bookmarkIdMapRef.current[messageId] = res.bookmark.id;
        }).catch(() => {
          // Revert on failure
          useChatStore.getState().toggleBookmarked(messageId, areaId, false);
          toast.error('Failed to bookmark message');
        });
      }
    },
    [areaId],
  );

  // Link preview hide handler
  const handleHideLinkPreview = useCallback(
    (messageId: string) => {
      toggleLinkPreview(messageId, true).catch(() => toast.error('Failed to hide link preview'));
    },
    [],
  );

  const canPin = isAdmin || currentOrg?.role === 'manager';

  // Media viewer handler
  const handleMediaClick = useCallback((attachment: MessageAttachment) => {
    setViewingMedia(attachment);
  }, []);

  // Scroll to bottom when new messages arrive (only if user is near bottom)
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (initialLoadDoneRef.current && messages.length > prevMessageCountRef.current) {
      const addedCount = messages.length - prevMessageCountRef.current;
      if (isNearBottomRef.current) {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
        // User is scrolled up — count new messages instead of auto-scrolling
        setNewMessageCount((prev) => prev + addedCount);
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length]);

  // Reset scroll-to-bottom state when switching areas
  useEffect(() => {
    setShowScrollToBottom(false);
    setNewMessageCount(0);
    isNearBottomRef.current = true;
    // Unread banner is reset in the initial load effect (captures new count)
    if (unreadBannerTimerRef.current) clearTimeout(unreadBannerTimerRef.current);
  }, [areaId]);

  // Handler for scroll-to-bottom button click
  const handleScrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollToBottom(false);
    setNewMessageCount(0);
  }, []);

  return (
    <>
    <div
      ref={chatContainerRef}
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag-and-drop overlay */}
      {isDragOver && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-blue-50/80 dark:bg-blue-950/80">
          <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-blue-400 bg-white/90 px-12 py-10 shadow-lg dark:border-blue-500 dark:bg-gray-900/90">
            <svg className="h-12 w-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">Drop files to upload</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Images, videos, and audio files</span>
          </div>
        </div>
      )}

      <ConnectionStatus />

      {/* Pinned messages bar */}
      {!isDm && <PinnedMessagesBar orgId={orgId} areaId={areaId} canUnpin={canPin} />}

      {/* Messages */}
      <div className="relative flex-1 overflow-hidden">
        {/* Voice Stream toggle — compact, top-right corner */}
        <button
          onClick={() => {
            setVoiceStreamEnabled((v) => !v);
            setAutoPlayMessageId(null);
          }}
          className={`absolute right-3 top-2 z-10 flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium transition-colors ${
            voiceStreamEnabled
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
              : 'bg-white/80 text-gray-500 hover:bg-gray-100 dark:bg-gray-800/80 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
          title={voiceStreamEnabled ? 'Voice Stream: ON' : 'Voice Stream: OFF'}
          aria-label={voiceStreamEnabled ? 'Disable voice stream' : 'Enable voice stream'}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
          Stream
        </button>

        {/* Unread messages banner */}
        <UnreadBanner count={unreadBannerCount} onDismiss={() => setUnreadBannerCount(0)} />

        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          role="log"
          aria-label="Messages"
          className="h-full overflow-y-auto"
        >
          {hasMore && (
            <div className="flex items-center justify-center gap-2 border-b border-gray-100 py-3 text-xs text-gray-400 dark:border-gray-800">
              {isLoadingRef.current ? (
                <>
                  <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading older messages...
                </>
              ) : (
                <div className="w-full" />
              )}
            </div>
          )}

          {messages.length === 0 && !initialLoadDone && (
            <MessageListSkeleton count={8} />
          )}

          {messages.length === 0 && initialLoadDone && (
            <EmptyState
              icon="chat"
              title="No messages yet"
              description="Start the conversation!"
              fillHeight
            />
          )}

          {messages.map((msg, idx) => (
            <div key={msg.tempId ?? msg.id}>
              <MessageItem
                message={msg}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onReply={handleReply}
                onRetry={handleRetry}
                onMediaClick={handleMediaClick}
                onReact={handleReact}
                onVoicePlaybackEnd={handleVoicePlaybackEnd}
                voiceAutoPlay={autoPlayMessageId === msg.id}
                onAcknowledge={handleAcknowledge}
                onViewReceipts={handleViewReceipts}
                onFlagReplyLater={handleFlagReplyLater}
                onReplyLaterWithSnooze={handleReplyLaterWithSnooze}
                onPin={handlePin}
                onBookmark={handleBookmark}
                onHideLinkPreview={handleHideLinkPreview}
                onViewThread={onViewThread}
                canPin={canPin}
                isAdmin={isManagerOrAbove}
                isReplyLater={replyLaterMessageIds.has(msg.id)}
                isGrouped={shouldGroupMessage(msg, messages[idx - 1])}
                viewStyle={viewStyle}
                orgSlug={params.orgSlug}
                areaSlug={params.areaSlug}
                customEmojiMap={customEmojiMap}
                customEmoji={customEmojiList}
              />
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Scroll to bottom floating button */}
        {showScrollToBottom && (
          <button
            onClick={handleScrollToBottom}
            className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-gray-200 bg-white/90 px-3 py-2 text-sm font-medium text-gray-700 shadow-lg backdrop-blur-sm transition-all hover:bg-white hover:shadow-xl dark:border-gray-600 dark:bg-gray-800/90 dark:text-gray-200 dark:hover:bg-gray-800"
            aria-label={newMessageCount > 0 ? `${newMessageCount} new message${newMessageCount === 1 ? '' : 's'} — scroll to bottom` : 'Scroll to bottom'}
          >
            {newMessageCount > 0 && (
              <span className="min-w-[20px] rounded-full bg-blue-500 px-1.5 py-0.5 text-center text-xs font-bold text-white">
                {newMessageCount > 99 ? '99+' : newMessageCount}
              </span>
            )}
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        )}
      </div>

      {/* Typing indicator */}
      <div aria-live="polite" className="flex-shrink-0">
        <TypingIndicator areaId={areaId} />
      </div>

      {/* Input */}
      <MessageInput areaId={areaId} orgId={orgId} onSend={handleSend} externalFileUpload={fileUpload} onSlashCommand={handleSlashCommand} />
    </div>

    {/* Media viewer */}
    {viewingMedia && (
      <MediaViewer
        attachment={viewingMedia}
        onClose={() => setViewingMedia(null)}
      />
    )}

    {/* DND confirmation dialog */}
    {dndConfirmOpen && dndUsers.length > 0 && (
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
        onClick={(e) => {
          // Dismiss on backdrop click (only when clicking the overlay itself)
          if (e.target === e.currentTarget) handleDndCancel();
        }}
      >
        <div className="w-full rounded-t-xl border border-gray-200 bg-white p-4 shadow-xl sm:mx-4 sm:max-w-sm sm:rounded-xl sm:p-6 dark:border-gray-700 dark:bg-gray-900">
          <h3 className="text-base font-semibold text-gray-900 sm:text-lg dark:text-gray-100">Notify users in Do Not Disturb?</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            The following mentioned users have notifications paused:
          </p>
          <ul className="mt-2 space-y-1">
            {dndUsers.map((u) => (
              <li key={u.userId} className="text-sm text-gray-700 dark:text-gray-300">
                {u.displayName} {u.isDnd ? '(DND)' : '(Outside working hours)'}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2 sm:justify-end">
            <button
              onClick={handleDndCancel}
              className="flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 sm:flex-initial sm:py-2 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleDndSendQuietly}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:flex-initial sm:py-2 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Send Quietly
            </button>
            <button
              onClick={handleDndNotifyAnyway}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 sm:flex-initial sm:py-2"
            >
              Notify Anyway
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Receipt detail modal (admin) */}
    <ReceiptDetailModal
      messageId={receiptMessageId ?? ''}
      open={!!receiptMessageId}
      onClose={() => setReceiptMessageId(null)}
    />

    {/* Slash command dialogs */}
    <SetStatusDialog open={showStatusDialog} onOpenChange={setShowStatusDialog} />
    <DndSettings open={showDndDialog} onOpenChange={setShowDndDialog} />

    </>
  );
});
